import React, { useState, useEffect, useMemo } from "react";
import { isValidUUID } from "../../utils/validation";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  Typography,
  Box,
  Checkbox,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";
import PeopleIcon from "@mui/icons-material/People";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { useBulkAssignUsersMutation } from "../../store/apis/companyApi";
import { useGetUsersQuery } from "../../store/apis/usersApi";
import {
  Company,
  CompanyWithBillings,
  IntegrationType,
} from "../../types/CompanyTypes";
import { integrationColors } from "../../utils/colorUtils";
import OrbLoader from "../common/OrbLoader";

interface CompanyTableProps {
  companiesData: (Company | CompanyWithBillings)[];
  maxHeight?: number | string;
  stickyHeader?: boolean;
  onView?: (company: Company | CompanyWithBillings) => void;
  isAdmin?: boolean;
  canBulkManage?: boolean;
}

type CompanyRow = Company | CompanyWithBillings;

// Integration chips shown in the integrations column, in display order
const INTEGRATION_FIELDS: {
  type: IntegrationType;
  ids: (company: CompanyRow) => unknown[] | null | undefined;
}[] = [
  { type: IntegrationType.DV360, ids: (c) => c.dv360AdvertiserIds },
  { type: IntegrationType.TABOOLA, ids: (c) => c.taboolaAccountIds },
  { type: IntegrationType.OUTBRAIN, ids: (c) => c.outbrainAccountIds },
  { type: IntegrationType.SPOTIFY, ids: (c) => c.spotifyAdAccountIds },
  { type: IntegrationType.HIVESTACK, ids: (c) => c.hivestackAdvertiserIds },
  { type: IntegrationType.AMAZON, ids: (c) => c.amazonAdvertiserIds },
  { type: IntegrationType.META, ids: (c) => c.metaAdAccountIds },
  { type: IntegrationType.TIKTOK, ids: (c) => c.tiktokAdvertiserIds },
  { type: IntegrationType.GOOGLE_ADS, ids: (c) => c.googleAdsCustomerIds },
  { type: IntegrationType.REDDIT, ids: (c) => c.redditAdAccountIds },
];

// Debounced (~250ms) copy of a search input
const useDebouncedValue = (value: string) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), 250);
    return () => clearTimeout(handle);
  }, [value]);
  return debounced;
};

const toggleInSet = (set: Set<string>, id: string) => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

const CompanyTable: React.FC<CompanyTableProps> = ({
  companiesData,
  maxHeight,
  stickyHeader = false,
  onView,
  isAdmin = false,
  canBulkManage = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Assign mode: company + user checkboxes, side-by-side tables
  const [assignMode, setAssignMode] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(
    new Set()
  );
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput);
  const [userSearchInput, setUserSearchInput] = useState("");
  const debouncedUserSearch = useDebouncedValue(userSearchInput);

  const { data: usersResponse, isLoading: isLoadingUsers } = useGetUsersQuery(
    undefined,
    { skip: !assignMode }
  );
  const [bulkAssignUsers, { isLoading: isAssigning }] =
    useBulkAssignUsersMutation();

  const filteredCompanies = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return companiesData;
    return companiesData.filter((c) =>
      [c.nameCommercial, c.nameFiscal, c.nif].some((field) =>
        (field ?? "").toLowerCase().includes(q)
      )
    );
  }, [companiesData, debouncedSearch]);

  const filteredUsers = useMemo(() => {
    const activeUsers = (usersResponse?.data ?? []).filter((u) => u.active);
    const q = debouncedUserSearch.trim().toLowerCase();
    if (!q) return activeUsers;
    return activeUsers.filter((u) =>
      [`${u.firstName} ${u.lastName}`, u.email].some((field) =>
        (field ?? "").toLowerCase().includes(q)
      )
    );
  }, [usersResponse, debouncedUserSearch]);

  // Valid (UUID) ids among currently visible/filtered rows
  const selectableCompanyIds = useMemo(
    () => filteredCompanies.map((c) => c.id).filter(isValidUUID),
    [filteredCompanies]
  );
  const selectableUserIds = useMemo(
    () => filteredUsers.map((u) => u.id),
    [filteredUsers]
  );
  const selectedVisibleCompanies = selectableCompanyIds.filter((id) =>
    selectedCompanies.has(id)
  ).length;
  const selectedVisibleUsers = selectableUserIds.filter((id) =>
    selectedUsers.has(id)
  ).length;

  const columnCount = assignMode ? 3 : 3 + (isAdmin ? 1 : 0);

  const exitAssignMode = () => {
    setAssignMode(false);
    setSelectedCompanies(new Set());
    setSelectedUsers(new Set());
    setUserSearchInput("");
  };

  const handleSelectCompany = (id: string) => {
    if (!isValidUUID(id)) {
      console.error("Invalid UUID format:", id);
      return;
    }
    setSelectedCompanies((prev) => toggleInSet(prev, id));
  };

  const handleAssign = async () => {
    try {
      const response = await bulkAssignUsers({
        companyIds: Array.from(selectedCompanies),
        userIds: Array.from(selectedUsers),
      }).unwrap();
      dispatch(
        setToastMessage({
          message: t("company.bulkAssign.success", {
            assignedCount: response.data.assignedCount,
            companyCount: response.data.companyCount,
          }),
          severity: "success",
        })
      );
      setSelectedCompanies(new Set());
      setSelectedUsers(new Set());
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        t("company.bulkAssign.error");
      dispatch(setToastMessage({ message, severity: "error" }));
    }
  };

  const headerCellSx = {
    backgroundColor: theme.palette.background.paper,
    fontWeight: 700,
  };
  const tableContainerSx = {
    boxShadow: "0px 8px 8px rgba(0, 0, 0, 0.04)",
    borderRadius: "12px",
    mb: 2,
  };
  const rowBackground = (selected: boolean, index: number) =>
    selected
      ? theme.palette.action.selected
      : index % 2 === 1
        ? theme.palette.neutral["100"]
        : "inherit";

  const companiesTable = (
    <TableContainer
      component={Paper}
      sx={{ ...tableContainerSx, maxHeight: assignMode ? "70vh" : maxHeight }}
    >
      <Table
        stickyHeader={stickyHeader || assignMode}
        aria-label="company table"
      >
        <TableHead>
          <TableRow>
            {assignMode && (
              <TableCell
                padding="checkbox"
                sx={{ backgroundColor: theme.palette.background.paper }}
              >
                <Checkbox
                  indeterminate={
                    selectedVisibleCompanies > 0 &&
                    selectedVisibleCompanies < selectableCompanyIds.length
                  }
                  checked={
                    selectableCompanyIds.length > 0 &&
                    selectedVisibleCompanies === selectableCompanyIds.length
                  }
                  onChange={(e) =>
                    setSelectedCompanies(
                      e.target.checked
                        ? new Set(selectableCompanyIds)
                        : new Set()
                    )
                  }
                />
              </TableCell>
            )}
            <TableCell sx={headerCellSx}>
              {t("company.table.nameCommercial")}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("company.table.nameFiscal")}
            </TableCell>
            {!assignMode && (
              <TableCell sx={headerCellSx}>{t("company.table.nif")}</TableCell>
            )}
            {!assignMode && isAdmin && (
              <TableCell sx={headerCellSx}>
                {t("company.table.integrations")}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredCompanies.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={columnCount}
                sx={{ textAlign: "center", py: 6 }}
              >
                <Typography
                  variant="pSmall"
                  color={theme.palette.neutral["600"]}
                >
                  {t("company.search.empty")}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {filteredCompanies.map((company, index) => {
            const selected = assignMode && selectedCompanies.has(company.id);
            const clickable = assignMode || !!onView;
            return (
              <TableRow
                key={company.id}
                onClick={() =>
                  assignMode
                    ? handleSelectCompany(company.id)
                    : onView?.(company)
                }
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  cursor: clickable ? "pointer" : "default",
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                  backgroundColor: rowBackground(selected, index),
                }}
              >
                {assignMode && (
                  <TableCell
                    padding="checkbox"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selected}
                      onChange={() => handleSelectCompany(company.id)}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {company.imageBase64 && (
                      <Box
                        component="img"
                        src={`data:image/png;base64,${company.imageBase64}`}
                        alt={company.nameCommercial}
                        sx={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          borderRadius: 9999,
                        }}
                      />
                    )}
                    {company.nameCommercial}
                  </Box>
                </TableCell>
                <TableCell>{company.nameFiscal}</TableCell>
                {!assignMode && <TableCell>{company.nif}</TableCell>}
                {!assignMode && isAdmin && (
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {INTEGRATION_FIELDS.filter(
                        ({ ids }) => (ids(company)?.length ?? 0) > 0
                      ).map(({ type }) => (
                        <Typography
                          key={type}
                          variant="caption"
                          sx={{
                            backgroundColor: integrationColors[type].background,
                            color: integrationColors[type].text,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                          }}
                        >
                          {type}
                        </Typography>
                      ))}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const usersTable = (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <TextField
        size="small"
        value={userSearchInput}
        onChange={(e) => setUserSearchInput(e.target.value)}
        placeholder={t("company.assignMode.searchUsers")}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <TableContainer
        component={Paper}
        sx={{ ...tableContainerSx, maxHeight: "70vh" }}
      >
        <Table stickyHeader aria-label="users table">
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{ backgroundColor: theme.palette.background.paper }}
              >
                <Checkbox
                  indeterminate={
                    selectedVisibleUsers > 0 &&
                    selectedVisibleUsers < selectableUserIds.length
                  }
                  checked={
                    selectableUserIds.length > 0 &&
                    selectedVisibleUsers === selectableUserIds.length
                  }
                  onChange={(e) =>
                    setSelectedUsers(
                      e.target.checked ? new Set(selectableUserIds) : new Set()
                    )
                  }
                />
              </TableCell>
              <TableCell sx={headerCellSx}>
                {t("company.assignMode.userName")}
              </TableCell>
              <TableCell sx={headerCellSx}>
                {t("company.assignMode.userEmail")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingUsers ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ textAlign: "center", py: 6 }}>
                  <OrbLoader size="inline" state="searching" />
                </TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ textAlign: "center", py: 6 }}>
                  <Typography
                    variant="pSmall"
                    color={theme.palette.neutral["600"]}
                  >
                    {t("company.assignMode.noUsers")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user, index) => {
                const selected = selectedUsers.has(user.id);
                return (
                  <TableRow
                    key={user.id}
                    onClick={() =>
                      setSelectedUsers((prev) => toggleInSet(prev, user.id))
                    }
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                      backgroundColor: rowBackground(selected, index),
                    }}
                  >
                    <TableCell
                      padding="checkbox"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selected}
                        onChange={() =>
                          setSelectedUsers((prev) => toggleInSet(prev, user.id))
                        }
                      />
                    </TableCell>
                    <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                    <TableCell sx={{ wordBreak: "break-all" }}>
                      {user.email}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const canAssign =
    selectedCompanies.size > 0 && selectedUsers.size > 0 && !isAssigning;

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1.5,
        }}
      >
        <TextField
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t("company.search.placeholder")}
          sx={{ flexGrow: 1, maxWidth: { sm: 420 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="pSmall" color={theme.palette.neutral["600"]}>
          {t("company.search.results", { count: filteredCompanies.length })}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {canBulkManage &&
          (assignMode ? (
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={exitAssignMode}
              disabled={isAssigning}
            >
              {t("common.cancel")}
            </Button>
          ) : (
            <Button
              variant="outlined"
              startIcon={<PeopleIcon />}
              onClick={() => setAssignMode(true)}
            >
              {t("company.bulkBar.assignUsers")}
            </Button>
          ))}
      </Box>

      {assignMode ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1fr) auto minmax(0, 1fr)",
            },
            gap: 2,
            alignItems: "start",
          }}
        >
          {companiesTable}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              position: { md: "sticky" },
              top: { md: 96 },
              pt: { md: 8 },
            }}
          >
            <Button
              variant="contained"
              onClick={handleAssign}
              disabled={!canAssign}
              startIcon={
                isAssigning ? (
                  <CircularProgress size={18} color="inherit" />
                ) : undefined
              }
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: "50px",
                textTransform: "none",
                fontFamily: "Nunito",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              {t("company.assignMode.assign")}
            </Button>
            <Typography
              variant="caption"
              color={theme.palette.neutral["600"]}
              sx={{ textAlign: "center" }}
            >
              {t("company.assignMode.companiesSelected", {
                count: selectedCompanies.size,
              })}
              <br />
              {t("company.assignMode.usersSelected", {
                count: selectedUsers.size,
              })}
            </Typography>
          </Box>
          {usersTable}
        </Box>
      ) : (
        companiesTable
      )}
    </Box>
  );
};

export default CompanyTable;
